import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';

import { type Sage100FormData, type Sage100TabType } from './constants.ts';
import Sage100IntegrationForm from './Sage100IntegrationForm.tsx';
import IntegrationBreadcrumb from '../../components/IntegrationBreadcrumb.tsx';
import Layout from '../../../core/components/Layout.tsx';
import Page from '../../../core/components/Page/index.tsx';
import SettingsNavigation from '../../../core/components/SettingsNavigation.tsx';
import Button from '../../../core/components/Button.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import { GET_SAGE100_INTEGRATIONS, UPDATE_SAGE100_INTEGRATION } from '../../queries.ts';
import { url } from '../../../core/utils/link.ts';
import { CUSTOMER_INTEGRATION_SAGE100_PATH } from '../../constants.ts';

const FORM_ID = 'sage100-integration-form';

const Sage100IntegrationEditPage = () => {
    const { t } = useTranslation('integrations');
    const { integrationId } = useParams();
    const { publishToast } = useToaster();
    const backTooltipText = `${t('backTooltipText')} ${t('erp.pageTitle')} / ${t('sage100.title')}`;
    const [activeTab, setActiveTab] = useState<Sage100TabType>('configuration');

    const { data, loading, refetch } = useQuery(GET_SAGE100_INTEGRATIONS, {
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: false,
    });
    const integration = data?.sage100Integrations?.find((i) => i.id === integrationId);

    const [updateIntegration, { loading: isSubmitting }] = useMutation(UPDATE_SAGE100_INTEGRATION);

    const handleSubmit = async (formData: Sage100FormData) => {
        try {
            await updateIntegration({
                variables: {
                    id: integration.id,
                    name: formData.name,
                    entitlementId: formData.entitlementId,
                    dataSet: formData.dataSet,
                    salesOrderType: formData.salesOrderType,
                    offerDocumentType: formData.offerDocumentType,
                    authFlow: formData.authFlow,
                    orderChannels: formData.orderChannelIds,
                    rfqChannels: formData.rfqChannelIds,
                    apiVersion: formData.apiVersion,
                    livePriceCheck: formData.livePriceCheck,
                    priceCheckBasis: formData.priceCheckBasis,
                    allowAgenticWrites: formData.allowAgenticWrites,
                    saveDocumentsAsDrafts: formData.saveDocumentsAsDrafts,
                    standardPriceList: formData.standardPriceList,
                    ignoreExpiredOffers: formData.ignoreExpiredOffers,
                    useOutputMapping: formData.useOutputMapping,
                },
            });
            await refetch();
            publishToast({ description: t('sage100.updateSuccess'), status: 'success' });
        } catch (error) {
            console.error(error);
            publishToast({ description: t('sage100.updateError'), status: 'error' });
        }
    };

    if (!loading && !integration) {
        return (
            <Layout>
                <SettingsNavigation />
                <Page className="flex-1">
                    <Page.Content lowered>
                        <p className="text-content-medium">{t('sage100.integrationNotFound')}</p>
                        <Button asChild className="mt-4">
                            <Link to={url(CUSTOMER_INTEGRATION_SAGE100_PATH)}>{t('sage100.form.cancelButton')}</Link>
                        </Button>
                    </Page.Content>
                </Page>
            </Layout>
        );
    }

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1">
                <Page.Header className="justify-between">
                    <IntegrationBreadcrumb
                        backTooltip={backTooltipText}
                        items={[
                            { label: t('sage100.title'), to: url(CUSTOMER_INTEGRATION_SAGE100_PATH) },
                            { label: integration?.name || t('sage100.form.updateTitle') },
                        ]}
                    />
                    <div className="flex gap-2">
                        <Button type="button" asChild>
                            <Link to={url(CUSTOMER_INTEGRATION_SAGE100_PATH)}>{t('sage100.form.cancelButton')}</Link>
                        </Button>
                        {activeTab === 'configuration' && (
                            <Button
                                variant="primary"
                                type="submit"
                                form={FORM_ID}
                                loading={isSubmitting}
                                disabled={loading || !integration}
                            >
                                {t('sage100.form.updateButton')}
                            </Button>
                        )}
                    </div>
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6">
                    {integration && (
                        <Sage100IntegrationForm
                            integration={integration}
                            onSubmit={handleSubmit}
                            activeTab={activeTab}
                            onTabChange={setActiveTab}
                            formId={FORM_ID}
                        />
                    )}
                </Page.Content>
            </Page>
        </Layout>
    );
};

export default Sage100IntegrationEditPage;
